import { useEffect } from 'react'

const POLL_INTERVAL_MS = 30_000
// Env values may carry stray whitespace/newlines from hosting dashboards.
// Trim before deriving anything, or the /api suffix strip silently no-ops
// and the redirect lands on /api/suspended (a JSON 403) instead of the
// public suspension page.
const API_BASE = (import.meta.env.VITE_API_URL || '/api').trim().replace(/\/+$/, '')
const API_ORIGIN = API_BASE.replace(/\/api$/, '')

/**
 * Client-side defense-in-depth for SCH-managed system suspension.
 *
 * The backend middleware already blocks full page loads and API calls while the
 * system is suspended. This hook catches the rare case where a user already has
 * the SPA loaded and tries to navigate client-side after suspension.
 *
 * It polls the unauthenticated heartbeat endpoint on the configured API origin
 * (VITE_API_URL — not the static SPA host) and redirects to the backend's
 * generic suspension page when SCH reports the system as suspended.
 */
export function useSystemStatus() {
  useEffect(() => {
    let timeoutId = null
    let cancelled = false

    async function check() {
      try {
        const res = await fetch(`${API_BASE}/system-status`, {
          cache: 'no-store',
        })
        if (!res.ok) return
        const data = await res.json().catch(() => ({}))
        if (data.status === 'suspended' && !window.location.pathname.startsWith('/suspended')) {
          window.location.replace(`${API_ORIGIN}/suspended`)
        }
      } catch {
        // Fail silently; the backend middleware is the authoritative gate.
      } finally {
        if (!cancelled) {
          timeoutId = setTimeout(check, POLL_INTERVAL_MS)
        }
      }
    }

    check()

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [])
}
